import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogActions, DialogContent, Stack } from '@mui/material';
import { FC, useEffect } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ContactDefaults, DialogTitleWithClose, IContact, IContactForm } from '../../../shared';
import { useUpdateContacts } from '../../hooks';
import { ContactInputs } from '../contact-inputs/contact-inputs.component';
import { useContactsSchema } from '../../validators/contacts.validator';
import { object } from 'yup';

interface Props {
    open: boolean;
    supportCase: string;
    contacts: IContact[];
    handleClose: () => void;
}

export const UpdateContactsModal: FC<Props> = ({ open, supportCase, contacts, handleClose }) => {
    const { t } = useTranslation();

    const { mutateAsync: updateContacts } = useUpdateContacts(supportCase);

    const form = useForm<IContactForm>({
        resolver: yupResolver(object().shape({ contacts: useContactsSchema().required() })),
        mode: 'all',
    });

    useEffect(() => {
        if (open && contacts) form.reset({ contacts: contacts });
    }, [open, contacts, form]);

    const {
        fields: contactFields,
        append: appendContact,
        remove: removeContact,
    } = useFieldArray({
        control: form.control,
        name: 'contacts',
        keyName: 'formId',
    });

    const onSubmit = async ({ contacts }: IContactForm) => {
        const mappedContacts = contacts.map((contact: IContact) => {
            return { ...contact, id: contact.id ? contact.id : undefined };
        });
        await updateContacts({ contacts: mappedContacts });
        handleClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <DialogTitleWithClose onClose={handleClose}>{t('editContactInformation')}</DialogTitleWithClose>
                    <DialogContent>
                        <Stack spacing={2} justifyContent="flex-start">
                            {contactFields.map((field, index) => (
                                <Stack key={field.formId}>
                                    <input type="hidden" {...form.register(`contacts.${index}.id`)} />
                                    <ContactInputs
                                        field={`contacts.${index}`}
                                        onRemoveContact={() => removeContact(index)}
                                    />
                                </Stack>
                            ))}

                            <Button
                                data-testid="add-contact"
                                onClick={() => appendContact(ContactDefaults)}
                                color="secondary"
                                sx={{ width: 'fit-content' }}
                            >
                                + {t('contactsAdd')}
                            </Button>
                        </Stack>
                    </DialogContent>

                    <DialogActions>
                        <Button type="submit" variant="contained" color="primary">
                            {t('save')}
                        </Button>
                        <Button onClick={handleClose} color="secondary">
                            {t('cancel')}
                        </Button>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
